import {useEffect, useState} from 'react';
import {
  MaterialReactTable,
  MRT_ToggleFiltersButton as ToggleFilters,
  MRT_ToggleFullScreenButton as ToggleFullScreen,
  useMaterialReactTable
} from 'material-react-table';
import {Box, Chip, IconButton, Tooltip, Typography} from '@mui/material';
import Breadcrumb from "./Breadcrumb";
import FolderIcon from "@mui/icons-material/Folder";
import RefreshIcon from '@mui/icons-material/Refresh';
import {formatBucketFolderItems, formatBuckets} from "./utils";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CopyAllIcon from '@mui/icons-material/CopyAll';
import {
  fetchDownloadItemS3Bucket, fetchS3BucketItems,
  fetchS3BucketReleaseNotes, fetchS3Buckets, fetchS3BucketSearchItems
} from "./api";
import QLIAlert from "./Alert";
import {useLocation, useNavigate} from "react-router-dom";
import CustomInputSearch from "./InputSearch";
import ShowNotes from "./ShowNotes";


const QLITable = () => {
  
  const navigate = useNavigate();
  const {pathname} = useLocation();
  
  const [rootFolder, setRootFolder] = useState(true);
  const [buckets, setBuckets] = useState([]);
  const [bucketData, setBucketData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [currentBucket, setCurrentBucket] = useState(null);
  const [currentBucketReleaseNotes, setCurrentBucketReleaseNotes] = useState('');
  const [currentPrefix, setCurrentPrefix] = useState(null);
  const [showAlert, setShowAlert] = useState(false);
  const [showAlertVariant, setShowAlertVariant] = useState('success');
  const [showAlertMessage, setShowAlertMessage] = useState('');
  const [reload, setReload] = useState(false);
  
  const redirectToLogin = () => {
    localStorage.setItem('postLoginRedirect', window.location.pathname);
    window.location.href = "/auth/login";
  };
  
  useEffect(() => {
    document.title = "QLI Artifacts | Home"
    const redirectPath = localStorage.getItem('postLoginRedirect');
    if (redirectPath && redirectPath !== window.location.pathname) {
      localStorage.removeItem('postLoginRedirect');
      window.location.replace(redirectPath);
    }
  }, []);
  
  const goHome = () => {
    setShowAlert(false);
    setCurrentBucket(null);
    setCurrentPrefix(null);
    setRootFolder(true);
    setLoading(true);
    setBreadcrumb([{"Home": goHome}]);
    fetchBuckets();
    setCurrentBucketReleaseNotes('');
  };
  
  const [breadCrumb, setBreadcrumb] = useState([{"Home": goHome}]);
  
  const fetchBuckets = () => {
    navigate(`/`);
    setShowAlert(false);
    setLoading(true);
    setIsError(false);
    setCurrentPrefix('');
    fetchS3Buckets().then(response => {
      setBuckets(formatBuckets(response.data.data.buckets));
      setLoading(false);
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setIsError(true);
      setLoading(false);
      setShowAlertVariant('danger');
      setShowAlertMessage(`Failed to retrieve buckets. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
  };
  
  const fetchBucketItems = (bucketName) => {
    // Reset manually applied column filters
    table.setColumnFilters([]);
    navigate(`/${bucketName}`);
    setShowAlert(false);
    setLoading(true);
    setIsError(false);
    setCurrentBucket(bucketName);
    setCurrentPrefix('');
    setBreadcrumb([{"Home": goHome}, {[bucketName]: () => fetchBucketItems(bucketName)}]);
    fetchS3BucketItems(bucketName).then(response => {
      if (!response.data.success) {
        setShowAlertVariant('danger');
        setShowAlertMessage(`Failed to retrieve bucket items. Error: ${response.data.http_code} ${response.data.error_details}`);
        setShowAlert(true);
        setLoading(false);
      } else {
        fetchS3BucketReleaseNotes(bucketName).then(response => {
          setCurrentBucketReleaseNotes(response.data.release_note);
        }).catch(error => {
          const status = error.response ? error.response.status : null;
          const msg = error.response.data ? error.response.data.detail : null;
          if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
            redirectToLogin();
            return;
          }
          setShowAlertVariant('danger');
          setShowAlertMessage(`Failed to retrieve release notes for bucket. Error: ${error.response.data.detail}`);
          setShowAlert(true);
        });
        setBucketData(formatBucketFolderItems(response.data.data, bucketName));
        setLoading(false);
      }
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setIsError(true);
      setLoading(false);
      setShowAlertVariant('danger');
      setShowAlertMessage(`Failed to retrieve bucket items. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
  };
  
  const fetchFolderItems = (bucketName, prefix, dontSetBreadCrumb = false) => {
    fetchS3BucketReleaseNotes(bucketName).then(response => {
      setCurrentBucketReleaseNotes(response.data.release_note);
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setShowAlertVariant('danger');
      setShowAlertMessage(`Failed to retrieve release notes for bucket. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
    // Reset manually applied column filters
    table.setColumnFilters([]);
    navigate(`/${bucketName}/${prefix}`);
    setShowAlert(false);
    setLoading(true);
    setIsError(false);
    setCurrentPrefix(prefix);
    if (!dontSetBreadCrumb) {
      setBreadcrumb(prev => {
        const idx = prev.findIndex(pref => {
          return Object.keys(pref)[0] === prefix;
        });
        if (idx !== -1) {
          return prev.slice(0, idx + 1);
        }
        return [...prev, {[prefix]: () => fetchFolderItems(bucketName, prefix)}];
      });
    }
    fetchS3BucketItems(bucketName, prefix).then(response => {
      setBucketData(formatBucketFolderItems(response.data.data, bucketName));
      setLoading(false);
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setIsError(true);
      setLoading(false);
      setShowAlertVariant('danger');
      setShowAlertMessage(`Failed to retrieve folder items. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
  };
  
  const serverSideSearch = (value) => {
    if (!value || value.trim() === '') {
      return;
    }
    setShowAlert(false);
    setLoading(true);
    setIsError(false);
    fetchS3BucketSearchItems(currentBucket, value, currentPrefix).then(response => {
      setBucketData(formatBucketFolderItems(response.data.data, currentBucket, true));
      setLoading(false);
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setIsError(true);
      setLoading(false);
      setShowAlertVariant('danger');
      setShowAlertMessage(`Failed to retrieve search items. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
  };
  
  const clearSearch = () => {
    setShowAlert(false);
    setLoading(true);
    setIsError(false);
    // fetch the current folder items again
    if (currentPrefix) {
      fetchFolderItems(currentBucket, currentPrefix);
    } else {
      fetchBucketItems(currentBucket);
    }
  };
  
  useEffect(() => {
      setShowAlert(false);
      fetchBuckets();
    },
    // eslint-disable-next-line
    [reload]);
  
  const viewBucket = (bucketName) => {
    setRootFolder(false);
    setBreadcrumb([{"Home": goHome}, {[bucketName]: () => fetchBucketItems(bucketName)}]);
    fetchBucketItems(bucketName);
  };
  
  const viewFolder = (pathName) => {
    setRootFolder(false);
    setBreadcrumb(prev => [...prev, {[pathName]: () => fetchFolderItems(currentBucket, pathName)}]);
    fetchFolderItems(currentBucket, pathName);
  };
  
  const copyLink = (item) => {
    setShowAlert(false);
    const itemKey = item.name;
    fetchDownloadItemS3Bucket(currentBucket, itemKey, currentPrefix, true).then(response => {
      navigator.clipboard.writeText(response.data.data.download_url).then(() => {
        setShowAlertVariant('success');
        setShowAlertMessage('Link has been copied to your clipboard.');
        setShowAlert(true);
      });
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setShowAlertVariant('danger');
      setShowAlertMessage(`Unable to copy the file download URL. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
  };
  
  const copyCurlCommand = (item) => {
    setShowAlert(false);
    const currentDns = window.location.origin;
    const filePath = currentPrefix ? `${currentPrefix}${item}` : item;
    const encodedBucketName = encodeURIComponent(currentBucket);
    const encodedFilePath = filePath
      ? filePath
        .split("/")
        .map((segment) => encodeURIComponent(segment))
        .join("/")
      : '';
    const url = `${currentDns}/${encodedBucketName}/${encodedFilePath}`;
    // const curlCommand = `curl -u USERNAME:PASSWORD -k -L -o ${item} ${url}`;
    navigator.clipboard.writeText(url).then(() => {
      setShowAlertVariant('success');
      setShowAlertMessage('URL has been copied to your clipboard.');
      setShowAlert(true);
    });
  };
  
  const downloadItem = (item) => {
    setShowAlert(false);
    const itemKey = item.name;
    fetchDownloadItemS3Bucket(currentBucket, itemKey, currentPrefix).then(response => {
      setShowAlertVariant('info');
      setShowAlertMessage('Download will begin shortly...');
      setShowAlert(true);
      // const openInNewTab = browserExtensions.some(ext => item.name.toLowerCase().endsWith(ext));
      window.open(response.data.data.download_url, '_blank', 'noopener,noreferrer');
    }).catch(error => {
      const status = error.response ? error.response.status : null;
      const msg = error.response.data ? error.response.data.detail : null;
      if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
        redirectToLogin();
        return;
      }
      setShowAlertVariant('danger');
      setShowAlertMessage(`Could not download the file. Error: ${error.response.data.detail}`);
      setShowAlert(true);
    });
  };
  
  const bucketColumns = [
    {
      accessorKey: 'name',
      header: 'Bucket Name',
      filterVariant: 'autocomplete',
      size: 450,
      Cell: ({cell}) => {
        const bucket = cell.getValue();
        return (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <span
            className="bucketNameStyle"
            onClick={e => {
              e.preventDefault();
              viewBucket(bucket);
            }}
          >
            <Box display="inline-flex" alignItems="center" gap={1}>
              <FolderIcon color="primary" fontSize="small"/>
              <Typography component="span">{bucket}</Typography>
            </Box>
          </span>
        );
      },
    },
    {
      accessorKey: 'creation_date',
      header: 'Creation Date',
      size: 240,
      filterFn: 'contains',
    }
  ];
  
  const bucketDataColumns = [
    {
      accessorKey: 'name',
      header: 'Name',
      filterVariant: 'autocomplete',
      size: 400,
      Cell: ({row, cell}) => {
        const item = row.original;
        const name = cell.getValue();
        const isFolder = item.type === 'folder';
        return (
          <button
            type="button"
            className="itemNameStyle"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              margin: 0,
              color: 'inherit',
              textAlign: 'left',
              cursor: isFolder ? 'pointer' : 'default'
            }}
            onClick={e => {
              e.preventDefault();
              if (isFolder) viewFolder(item.path);
            }}
          >
            <Box display="inline-flex" alignItems="center" gap={1}>
              {isFolder ? <FolderIcon color="primary" fontSize="small"/> :
                <InsertDriveFileIcon fontSize="small"/>}
              <Typography component="span">{name}</Typography>
            </Box>
          </button>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      size: 120,
      filterFn: 'contains',
      Cell: ({cell}) => {
        const t = cell.getValue();
        return <Chip size="small" label={t} color={t === 'folder' ? 'primary' : 'default'}/>;
      },
    },
    {
      accessorKey: 'size',
      header: 'Size',
      size: 140,
      filterFn: 'contains',
    },
    {
      accessorKey: 'last_modified',
      header: 'Last Modified',
      size: 240,
      filterFn: 'contains',
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      enableSorting: false, // disable sorting for this column
      enableColumnFilter: false, // disable filtering for this column
      size: 180,
      Cell: ({row}) => {
        const item = row.original;
        if (item.type === 'folder') return '-';
        return (
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Copy URL">
              <IconButton size="small" onClick={() => copyCurlCommand(item.name)} className="actionBtn copyCurlBtn">
                <CopyAllIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
            <Tooltip title="Download File">
              <IconButton size="small" onClick={() => downloadItem(item)} className="actionBtn downloadBtn">
                <CloudDownloadIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
            <Tooltip title="Copy PreSigned URL">
              <IconButton size="small" onClick={() => copyLink(item)} className="actionBtn copyPresignedBtn">
                <ContentCopyIcon fontSize="small"/>
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];
  
  const columns = rootFolder ? bucketColumns : bucketDataColumns;
  const data = rootFolder ? buckets : bucketData;
  
  // check if the requested path has bucket name & prefix
  useEffect(
    () => {
      async function handleBrowserUrlHit() {
        const pathParts = pathname.split('/').filter(part => part && part !== 'download');
        if (pathParts.length >= 1) {
          const bucketName = pathParts[0];
          const restPrefix = pathParts.slice(1);
          setCurrentBucket(bucketName);
          setRootFolder(false);
          if (pathParts.length === 1) {
            fetchBucketItems(bucketName);
          } else {
            // Need to check if the last part is a file and call download API
            const lastPart = pathParts.at(-1);
            const isFile = !pathname.endsWith("/") && lastPart && lastPart.includes('.')
            const prefix = (restPrefix.length === 1 && restPrefix[0] === lastPart) ? "" : restPrefix.slice(0, -1).join('/') + "/";
            if (isFile) {
              try {
                const response = await fetchDownloadItemS3Bucket(bucketName, lastPart, prefix);
                setShowAlertVariant('info');
                setShowAlertMessage('Download will begin shortly...');
                setShowAlert(true);
                window.open(response.data.data.download_url, '_self', 'noopener,noreferrer');
              } catch (error) {
                const status = error.response ? error.response?.status : null;
                const msg = error.response?.data ? error.response?.data?.detail : null;
                if (status === 401 && msg && (msg.toLowerCase().includes('session expired') || msg.toLowerCase().includes('unauthenticated'))) {
                  redirectToLogin();
                  return;
                }
                setShowAlertVariant('danger');
                setShowAlertMessage(`Could not download the file. Error: ${error.response?.data?.detail}`);
                setShowAlert(true);
              } finally {
                setBreadcrumb(() => {
                  return [
                    {"Home": goHome},
                    {[bucketName]: () => fetchBucketItems(bucketName)},
                    ...restPrefix.map((eachPrefix, idx) => {
                      const cumulativePrefix = restPrefix.slice(0, idx + 1).join('/') + "/";
                      return {[cumulativePrefix]: () => fetchFolderItems(bucketName, cumulativePrefix)};
                    })
                  ];
                });
                setCurrentPrefix(prefix);
                setRootFolder(false);
                if (prefix) {
                  fetchFolderItems(bucketName, prefix);
                } else {
                  fetchBucketItems(bucketName);
                }
              }
            } else {
              const prefix = restPrefix.join('/') + "/";
              setBreadcrumb(prev => {
                const idx = prev.findIndex(pref => Object.keys(pref)[0] === prefix);
                if (idx !== -1) {
                  return prev.slice(0, idx + 1);
                }
                return [
                  {"Home": goHome},
                  {[bucketName]: () => fetchBucketItems(bucketName)},
                  ...restPrefix.map((eachPrefix, idx) => {
                    const cumulativePrefix = restPrefix.slice(0, idx + 1).join('/') + "/";
                    return {[cumulativePrefix]: () => fetchFolderItems(bucketName, cumulativePrefix)};
                  })
                ];
              });
              setCurrentPrefix(prefix);
              fetchFolderItems(bucketName, prefix, true);
            }
          }
        }
      }
      
      handleBrowserUrlHit();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  
  
  const table = useMaterialReactTable({
    data,
    columns,
    muiTableContainerProps: {sx: {height: '60vh'}},
    muiFilterTextFieldProps: {
      className: 'column-filters',
      size: 'small',
      variant: 'outlined',
    },
    positionToolbarAlertBanner: 'top',
    muiToolbarAlertBannerProps: isError
      ? {
        color: 'error',
        children: 'Error loading data, Please try again.',
      }
      : undefined,
    muiTableHeadCellProps: {
      sx: {fontSize: '14px', backgroundColor: "#496ab2", color: "white"},
    },
    muiTableBodyCellProps: {
      sx: {fontSize: '14px'},
    },
    muiTableBodyProps: {
      sx: (theme) => {
        if (!data || data.length === 0) {
          return {};
        }
        const baseBackgroundColor = theme.palette.background.default;
        return {
          '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]) > td': {
            backgroundColor: theme.palette.action.hover,
          },
          '& tr:nth-of-type(odd):not([data-selected="true"]):not([data-pinned="true"]):hover > td': {
            backgroundColor: theme.palette.action.selected,
          },
          '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]) > td': {
            backgroundColor: baseBackgroundColor,
          },
          '& tr:nth-of-type(even):not([data-selected="true"]):not([data-pinned="true"]):hover > td': {
            backgroundColor: theme.palette.action.selected,
          },
        };
      },
    },
    state: {
      isLoading: loading,
      showProgressBars: loading,
      showSkeletons: loading,
      showAlertBanner: isError,
    },
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableGrouping: false,
    enableColumnPinning: false,
    enableColumnActions: false,
    enableFacetedValues: true,
    enableRowActions: false,
    enableRowSelection: false,
    enableDensityToggle: false,
    enableHiding: false,
    enableStickyHeader: true,
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: true,
      density: 'compact',
      pagination: {
        pageSize: 20,
      },
    },
    localization: {
      noRecordsToDisplay: data && data.length > 0 ? '' : 'No items to display',
    },
    // Customizing the top toolbar
    renderTopToolbar: () => (
      <Box
        sx={{
          display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: '1rem', justifyContent: 'space-between',
          alignItems: 'center', p: '0.8rem',
        }}
      >
        {/* Show Global Filter only for buckets */}
        {!rootFolder ? <Box sx={{display: 'flex', alignItems: 'center'}}>
          <CustomInputSearch search={serverSideSearch} clear={clearSearch}/>
        </Box> : <Box></Box>}
        <Box>
          {/* Show Refresh Button only for getting buckets data */}
          {rootFolder &&
            <Tooltip arrow title="Refresh Data">
              <IconButton onClick={() => setReload(!reload)}>
                <RefreshIcon/>
              </IconButton>
            </Tooltip>}
          <ToggleFilters table={table}/>
          <ToggleFullScreen table={table}/>
        </Box>
      </Box>
    ),
    muiSearchTextFieldProps: {
      placeholder: 'Search any file name in the bucket',
      size: 'small',
      sx: {width: '20rem'},
      variant: 'outlined',
    },
    muiPaginationProps: {
      color: 'primary',
      rowsPerPageOptions: [10, 20, 50, 100, 150, 200],
      shape: 'rounded',
      variant: 'outlined',
    },
  });
  
  return <>
    {showAlert && <QLIAlert variant={showAlertVariant} message={showAlertMessage}/>}
    <Breadcrumb words={breadCrumb}/>
    {currentBucket && currentBucket.length > 0 && currentBucketReleaseNotes.length > 0 &&
      <ShowNotes bucket={currentBucket} text={currentBucketReleaseNotes}/>}
    <MaterialReactTable table={table}/>
  </>;
};

export default QLITable;
